import { useMemo } from 'react';
import type { TimesheetEntry } from '@/types/timesheet';
import type { DayName } from '../timesheetGridUtils';

export interface UseTimesheetTotalsParams {
  entries: TimesheetEntry[];
  visibleDays: DayName[];
}

export interface UseTimesheetTotalsResult {
  rowTotals: number[];
  columnTotals: number[];
  weekTotal: number;
}

/** Per-row, per-day and whole-week hour totals for the visible day columns. */
export function useTimesheetTotals({ entries, visibleDays }: UseTimesheetTotalsParams): UseTimesheetTotalsResult {
  const rowTotals = useMemo(() => {
    return entries.map(entry =>
      visibleDays.reduce((sum, day) => sum + (entry.hours[day] || 0), 0)
    );
  }, [entries, visibleDays]);

  const columnTotals = useMemo(() => {
    return visibleDays.map(day =>
      entries.reduce((sum, entry) => sum + (entry.hours[day] || 0), 0)
    );
  }, [entries, visibleDays]);

  const weekTotal = useMemo(
    () => columnTotals.reduce((sum, total) => sum + total, 0),
    [columnTotals]
  );

  return { rowTotals, columnTotals, weekTotal };
}
